import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { apiService } from '../services/api';
import { useTenantStore } from '../stores/useTenantStore';
import type {
  CompleteGenerationRunRequest,
  GenerationRun,
  GenerationRunFilter,
  StartGenerationRunRequest,
} from '../types';

export function useGenerationRuns(filter: GenerationRunFilter = {}) {
  return useQuery<GenerationRun[]>({
    queryKey: ['generation-runs', filter],
    queryFn: () => apiService.getGenerationRuns(filter),
    enabled: Boolean(filter.tenant_id),
    refetchInterval: 30000,
  });
}

export function useLiveRuns() {
  const currentTenant = useTenantStore((state) => state.currentTenant);
  return useQuery<GenerationRun[]>({
    queryKey: ['generation-runs', 'live', currentTenant],
    queryFn: () => apiService.getGenerationRuns({ status: 'running', sort: 'date_desc', tenant_id: currentTenant || undefined }),
    refetchInterval: 30000,
  });
}

export function useGenerationRun(runId: string | null) {
  return useQuery<GenerationRun>({
    queryKey: ['generation-runs', runId],
    queryFn: () => apiService.getGenerationRun(runId!),
    enabled: Boolean(runId),
    refetchInterval: 30000,
  });
}

export function useStartGenerationRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: StartGenerationRunRequest) => apiService.startGenerationRun(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['generation-runs'] });
    },
  });
}

export function useCompleteGenerationRun(successMessage?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ runId, data }: { runId: string; data: CompleteGenerationRunRequest }) =>
      apiService.completeGenerationRun(runId, data),
    onSuccess: () => {
      if (successMessage) {
        message.success(successMessage);
      }
      queryClient.invalidateQueries({ queryKey: ['generation-runs'] });
    },
    onError: (error) => {
      console.error('Failed to update generation run', error);
      message.error('Unable to update generation run');
    },
  });
}
